import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navigation from '../components/Navigation';
import { useOwners } from '../hooks/useOwners';
import { filterOwnersBySearch, sortOwners } from '../utils/ownerUtils';
import { paginateArray } from '../utils/dataUtils';
import { UI_CONFIG } from '../constants/config';
import { PointsService } from '../services/pointsService';
import '../styles/pages/owners.css';

const AVATAR_PALETTES = [
    { bg: '#FFF0F6', color: '#be185d' },
    { bg: '#EEF7FF', color: '#1d4ed8' },
    { bg: '#EDFAF4', color: '#166534' },
    { bg: '#FFF5E6', color: '#92400e' },
    { bg: '#F0EDFF', color: '#4C35C4' },
    { bg: '#E0FDF4', color: '#065f46' },
];

function getOwnerStyle(name) {
    const idx = Math.max(0, ((name || 'A').toUpperCase().charCodeAt(0) - 65) % AVATAR_PALETTES.length);
    return AVATAR_PALETTES[idx];
}

function getInitials(name) {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return parts.length >= 2
        ? (parts[0][0] + parts[1][0]).toUpperCase()
        : parts[0].slice(0, 2).toUpperCase();
}

export default function Owners() {
    const navigate = useNavigate();
    const { role, authorizedFetch } = useAuth();
    const {
        owners,
        loading,
        error,
        validationErrors,
        createOwner,
        updateOwner,
        deleteOwner,
        clearError
    } = useOwners();

    const [pointBalances, setPointBalances] = useState({});

    useEffect(() => {
        if (!authorizedFetch) return;
        PointsService.getAllBalances(authorizedFetch)
            .then(setPointBalances)
            .catch(() => {});
    }, [authorizedFetch]);

    // UI State
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [sortBy, setSortBy] = useState('name');
    const [sortDirection, setSortDirection] = useState('asc');
    const [editingId, setEditingId] = useState(null);
    const [showForm, setShowForm] = useState(false);

    // Form State
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        address: ''
    });

    // Computed values
    const filteredOwners = filterOwnersBySearch(owners, search);
    const sortedOwners = sortOwners(filteredOwners, sortBy, sortDirection);
    const paginationResult = paginateArray(
        sortedOwners,
        currentPage,
        UI_CONFIG.PAGINATION.OWNERS_PER_PAGE
    );

    const handleFormChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (validationErrors[field]) clearError();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        clearError();
        try {
            if (editingId) {
                await updateOwner(editingId, formData);
            } else {
                await createOwner(formData);
            }
            resetForm();
        } catch (err) {}
    };

    const handleEdit = (owner) => {
        setEditingId(owner._id);
        setFormData({
            name: owner.name,
            email: owner.email,
            phone: owner.phone,
            address: owner.address || ''
        });
        setShowForm(true);
        clearError();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (ownerId) => {
        if (!window.confirm('¿Eliminar este propietario?')) return;
        try {
            await deleteOwner(ownerId);
        } catch (err) {}
    };

    const handleSearchChange = (searchTerm) => {
        setSearch(searchTerm);
        setCurrentPage(1);
    };

    const resetForm = () => {
        setFormData({ name: '', email: '', phone: '', address: '' });
        setEditingId(null);
        setShowForm(false);
        clearError();
    };

    const hasFilters = search !== '';

    return (
        <div className="owners-page">
            <Navigation />

            <div className="owners-container">
                {/* Page header */}
                <div className="owners-page-header">
                    <h1>Propietarios</h1>
                </div>

                {/* Tab navigation — mirrors Dogs page */}
                <div className="tab-navigation">
                    <button onClick={() => navigate('/dogs?tab=overview')} className="tab-button">📋 Resumen</button>
                    <button onClick={() => navigate('/dogs?tab=dog-form')} className="tab-button">➕ Agregar perro</button>
                    <button onClick={() => navigate('/dogs?tab=all-dogs')} className="tab-button">🐾 Todos los perros</button>
                    <button className="tab-button active">👥 Ver propietarios</button>
                    <button
                        onClick={() => { resetForm(); setShowForm(s => !s); }}
                        className={`tab-button${showForm && !editingId ? ' active' : ''}`}
                    >
                        {editingId ? '✏️ Editar propietario' : '👤 Agregar propietario'}
                    </button>
                </div>

                {/* Error */}
                {error && <div className="owners-error">{error}</div>}

                {/* Add / Edit Form */}
                {showForm && (
                    <div className="owner-form">
                        <h2>{editingId ? 'Editar propietario' : 'Agregar propietario'}</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="form-row">
                                <div className="form-field">
                                    <label>Nombre</label>
                                    <input
                                        value={formData.name}
                                        onChange={e => handleFormChange('name', e.target.value)}
                                        required
                                        className={validationErrors.name ? 'error' : ''}
                                    />
                                    {validationErrors.name && <span className="error-text">{validationErrors.name}</span>}
                                </div>
                                <div className="form-field">
                                    <label>Teléfono</label>
                                    <input
                                        value={formData.phone}
                                        onChange={e => handleFormChange('phone', e.target.value)}
                                        required
                                        className={validationErrors.phone ? 'error' : ''}
                                    />
                                    {validationErrors.phone && <span className="error-text">{validationErrors.phone}</span>}
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-field">
                                    <label>Correo electrónico</label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={e => handleFormChange('email', e.target.value)}
                                        required
                                        className={validationErrors.email ? 'error' : ''}
                                    />
                                    {validationErrors.email && <span className="error-text">{validationErrors.email}</span>}
                                </div>
                                <div className="form-field">
                                    <label>Dirección</label>
                                    <input
                                        value={formData.address}
                                        onChange={e => handleFormChange('address', e.target.value)}
                                        maxLength={60}
                                        placeholder="Ingresa la dirección"
                                        required
                                        className={validationErrors.address ? 'error' : ''}
                                    />
                                    {validationErrors.address && <span className="error-text">{validationErrors.address}</span>}
                                    <small className="char-count">{formData.address.length}/60</small>
                                </div>
                            </div>
                            <div className="form-actions">
                                <button type="submit" disabled={loading}>
                                    {loading ? 'Guardando...' : (editingId ? 'Actualizar' : 'Crear')}
                                </button>
                                <button type="button" onClick={resetForm}>Cancelar</button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Search & Filter bar */}
                <div className="dogs-header-section">
                    <div className="dogs-filters">
                        <div className="filter-group">
                            <label>Buscar</label>
                            <input
                                type="text"
                                value={search}
                                onChange={e => handleSearchChange(e.target.value)}
                                placeholder="Buscar por nombre, email o teléfono..."
                            />
                        </div>
                        <div className="filter-group">
                            <label>Ordenar por</label>
                            <select
                                value={`${sortBy}-${sortDirection}`}
                                onChange={e => {
                                    const [field, dir] = e.target.value.split('-');
                                    setSortBy(field);
                                    setSortDirection(dir);
                                    setCurrentPage(1);
                                }}
                            >
                                <option value="name-asc">Nombre (A-Z)</option>
                                <option value="name-desc">Nombre (Z-A)</option>
                                <option value="email-asc">Email (A-Z)</option>
                                <option value="email-desc">Email (Z-A)</option>
                            </select>
                        </div>
                        {hasFilters && (
                            <button
                                className="clear-filters-btn"
                                onClick={() => { setSearch(''); setCurrentPage(1); }}
                            >
                                Limpiar filtros
                            </button>
                        )}
                    </div>
                </div>

                {/* Owner Grid */}
                {loading ? (
                    <div className="loading">Cargando propietarios...</div>
                ) : paginationResult.items.length === 0 ? (
                    <div className="no-owners">
                        <p>{search ? 'No se encontraron propietarios con tu búsqueda.' : 'No hay propietarios registrados.'}</p>
                    </div>
                ) : (
                    <>
                        <div className="owners-count">
                            {paginationResult.totalItems} propietario{paginationResult.totalItems !== 1 ? 's' : ''}
                        </div>
                        <div className="owners-grid">
                            {paginationResult.items.map(owner => {
                                const style = getOwnerStyle(owner.name);
                                const initials = getInitials(owner.name);
                                const pts = pointBalances[owner._id] ?? 0;
                                return (
                                    <div key={owner._id} className="owner-card">
                                        <div className="owner-header">
                                            <div
                                                className="owner-avatar"
                                                style={{ backgroundColor: style.bg, color: style.color }}
                                            >
                                                {initials}
                                            </div>
                                            <div className="owner-title">
                                                <h3>{owner.name}</h3>
                                            </div>
                                            <div
                                                className="owner-initial-badge"
                                                style={{ backgroundColor: style.bg, color: style.color }}
                                            >
                                                {owner.name?.split(' ')[0] || 'Propietario'}
                                            </div>
                                        </div>

                                        <div className="owner-info">
                                            {pts > 0 && (
                                                <p className="owner-cashback-row">
                                                    <span>⭐ Cashback</span>
                                                    <span className="owner-points-badge">{pts.toFixed(2)} pts</span>
                                                </p>
                                            )}
                                            <p><span>📞</span> {owner.phone}</p>
                                            <p><span>✉️</span> {owner.email}</p>
                                            {owner.address && <p><span>📍</span> {owner.address}</p>}
                                        </div>

                                        <div className="card-actions">
                                            <button
                                                className="view-details-btn"
                                                onClick={() => navigate(`/owners/${owner._id}`)}
                                            >
                                                Ver perfil
                                            </button>
                                            <button
                                                className="edit-btn"
                                                onClick={() => handleEdit(owner)}
                                                disabled={loading}
                                            >
                                                Editar
                                            </button>
                                            <button
                                                className="delete-btn"
                                                onClick={() => handleDelete(owner._id)}
                                                disabled={loading}
                                            >
                                                Eliminar
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}

                {/* Pagination */}
                {paginationResult.totalPages > 1 && (
                    <div className="owners-pagination">
                        <button
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={!paginationResult.hasPrevPage}
                        >
                            Anterior
                        </button>
                        <span>Página {paginationResult.currentPage} de {paginationResult.totalPages}</span>
                        <button
                            onClick={() => setCurrentPage(prev => Math.min(paginationResult.totalPages, prev + 1))}
                            disabled={!paginationResult.hasNextPage}
                        >
                            Siguiente
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
